import JSZip from 'jszip';
import QRCode from 'qrcode';
import { montarUrlIdentificador } from './url-identificador';
import type { IdentificadorLoja } from '../api';

const ROTULO_TIPO: Record<string, string> = {
  MESA: 'Mesa',
  COMANDA: 'Comanda',
  TOTEM: 'Totem',
  TOTEM_CHECKOUT: 'Totem Checkout',
  TABLET: 'Tablet',
  DELIVERY: 'Delivery',
  VITRINE: 'Vitrine',
};

/** Remove caracteres inválidos para nome de arquivo (Windows/macOS/Linux). */
function nomeArquivoSeguro(base: string): string {
  return base.replace(/[\\/:*?"<>|]/g, '-').replace(/\s+/g, ' ').trim();
}

/**
 * Gera um ZIP com o QR code (PNG) de cada identificador e dispara o download no
 * navegador. Cada QR codifica a MESMA URL de acesso exibida na tela; o arquivo é
 * nomeado por tipo + número (ex.: "Mesa 1.png"), pronto para enviar à gráfica
 * (cavaletes/adesivos). Resolução alta (1024px) para boa impressão.
 *
 * Retorna a quantidade de QR codes gerados.
 */
export async function baixarQrCodesZip(itens: IdentificadorLoja[], nomeZip: string): Promise<number> {
  const zip = new JSZip();
  const usados = new Set<string>();

  for (const item of itens) {
    const url = montarUrlIdentificador(item);
    const rotulo = ROTULO_TIPO[item.tipo] ?? item.tipo;
    const nomeBase = nomeArquivoSeguro(`${rotulo} ${item.numero ?? item.codigo}`);

    // Evita colisão de nomes (ex.: dois pontos sem número): sufixa (2), (3)...
    let nome = nomeBase;
    let n = 2;
    while (usados.has(nome.toLowerCase())) nome = `${nomeBase} (${n++})`;
    usados.add(nome.toLowerCase());

    const dataUrl = await QRCode.toDataURL(url, { width: 1024, margin: 2, errorCorrectionLevel: 'M' });
    // dataUrl = "data:image/png;base64,<...>"; JSZip grava o base64 direto.
    const base64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
    zip.file(`${nome}.png`, base64, { base64: true });
  }

  await baixarZip(zip, nomeZip);
  return itens.length;
}

async function baixarZip(zip: JSZip, nomeZip: string): Promise<void> {
  const blob = await zip.generateAsync({ type: 'blob' });
  const href = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = nomeZip;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(href);
  }
}

/**
 * Desenha o QR com o rótulo legível embaixo e devolve o PNG em base64.
 *
 * O número impresso não é enfeite: é ele que o garçom lê para saber qual comanda
 * é aquela, e é por ele que a loja confere a etiqueta na hora de colar. Sem o
 * rótulo dentro da imagem o número só existe no nome do arquivo — que a gráfica
 * não imprime.
 */
async function pngComRotulo(conteudo: string, rotulo: string): Promise<string> {
  const LADO = 1024;
  const FAIXA = 180;

  const dataUrl = await QRCode.toDataURL(conteudo, { width: LADO, margin: 2, errorCorrectionLevel: 'M' });

  const canvas = document.createElement('canvas');
  canvas.width = LADO;
  canvas.height = LADO + FAIXA;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl.slice(dataUrl.indexOf(',') + 1);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const img = new Image();
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = () => reject(new Error('falha ao desenhar o QR'));
    img.src = dataUrl;
  });
  ctx.drawImage(img, 0, 0, LADO, LADO);

  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = 'bold 120px Arial, Helvetica, sans-serif';
  ctx.fillText(rotulo, LADO / 2, LADO + FAIXA / 2, LADO - 40);

  const png = canvas.toDataURL('image/png');
  return png.slice(png.indexOf(',') + 1);
}

/**
 * ZIP com as etiquetas das COMANDAS cadastradas no Sischef, para mandar à
 * gráfica. Cada PNG traz o QR e o número por extenso.
 *
 * O conteúdo do código é o número da comanda: não é URL, então ler com o
 * celular não abre o cardápio — a etiqueta serve para o tablet e mais nada.
 */
export async function baixarEtiquetasComandaZip(
  comandas: Array<{ numero: string; etiqueta: string }>,
  nomeZip: string,
  /** Progresso do desenho, para a tela não ficar muda. Cada PNG é um canvas de
   *  1024px: com centenas de comandas isso leva dezenas de segundos. */
  aoProgresso?: (feitas: number, total: number) => void,
): Promise<number> {
  const zip = new JSZip();
  const usados = new Set<string>();
  let feitas = 0;

  for (const comanda of comandas) {
    const nomeBase = nomeArquivoSeguro(`Comanda ${comanda.numero}`);
    let nome = nomeBase;
    let n = 2;
    while (usados.has(nome.toLowerCase())) nome = `${nomeBase} (${n++})`;
    usados.add(nome.toLowerCase());

    zip.file(`${nome}.png`, await pngComRotulo(comanda.etiqueta, `COMANDA ${comanda.numero}`), {
      base64: true,
    });

    feitas++;
    if (aoProgresso) {
      aoProgresso(feitas, comandas.length);
      // Devolve o fio para o navegador pintar o contador — sem isto o laço
      // segura a thread e a tela só atualiza no fim, quando não serve mais.
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  await baixarZip(zip, nomeZip);
  return comandas.length;
}

